import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { InputBaseProps } from '@mui/material/InputBase';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import * as React from 'react';
import { ReactNode, useId } from 'react';
import { useFormContext } from 'react-hook-form';

import { CustomShadows } from '@/theme';

import { getCalculatorDatePickerLabelStyles, getCalculatorSelectStyles } from './CalculatorStyles';

interface CalculatorInputsProps {
  title?: string;
  sx?: SxProps;
  children: React.ReactNode;
}

export const CalculatorInputs = ({
  title = 'Select loan terms',
  sx = {},
  children,
}: CalculatorInputsProps) => {
  const theme = useTheme();
  const { reset } = useFormContext();

  return (
    <Box
      sx={{
        p: 3,
        flexGrow: 1,
        borderRadius: 6,
        boxShadow: (theme.shadows as CustomShadows).d,
        background: theme.palette.purple[100],
        borderTopRightRadius: { xs: theme.shape.borderRadius * 6, md: 0 },
        borderBottomLeftRadius: { xs: 0, md: theme.shape.borderRadius * 6 },
        borderBottomRightRadius: 0,
        position: 'relative',
        width: '100%',
        maxWidth: {
          xs: `calc(100% - ${theme.spacing(3)} * 2)`,
          md: '55%',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          // make a right triangle
          top: {
            xs: 'unset',
            md: 0,
          },
          bottom: {
            xs: '-2.48rem',
            md: 'unset',
          },
          right: {
            xs: 0,
            md: '-2.48rem',
          },
          left: {
            xs: 0,
            md: 'unset',
          },
          width: {
            xs: '100%',
            md: '2.5rem',
          },
          height: {
            xs: '2.5rem',
            md: '100%',
          },
          background: {
            xs: `radial-gradient(ellipse at bottom left, ${theme.palette.purple[100]} 1%, transparent 55%),
            radial-gradient(ellipse at bottom right, ${theme.palette.purple[100]} 1%, transparent 55%),
            ${theme.palette.purple[100]}`,
            md: `radial-gradient(ellipse at top left, ${theme.palette.purple[100]} 1%, transparent 55%),
            radial-gradient(ellipse at bottom left, ${theme.palette.purple[100]} 1%, transparent 55%),
            ${theme.palette.purple[100]}`,
          },
          clipPath: {
            xs: 'polygon(0 0, 100% 0, 50% 100%)',
            md: 'polygon(0 0, 100% 50%, 0 100%)',
          },
          zIndex: 1,
        },
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography
          variant="h3"
          sx={{
            color: theme.palette.text.primary,
            textTransform: 'uppercase',
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={() => reset()}
          sx={{ color: theme.palette.purple[900], textTransform: 'none', flexShrink: 0 }}
        >
          Reset
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const StyledRow = ({ children, sx = {} }: { children: ReactNode; sx?: SxProps }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export const StyledColumn = ({ children, sx = {} }: { children: ReactNode; sx?: SxProps }) => {
  return (
    <Box
      data-component="StyledColumn"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        gap: 2,
        maxWidth: '100%',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export const Styled2ColGrid = ({ children, sx = {} }: { children: ReactNode; sx?: SxProps }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const StyledLabel = ({
  fieldId,
  label,
  helperText = undefined,
}: {
  label: string;
  fieldId: string;
  helperText?: string | undefined;
}) => {
  const theme = useTheme();
  return (
    <Box
      component="label"
      htmlFor={fieldId}
      sx={{
        color: `${theme.palette.purple[900]}DD`,
        fontWeight: 600,
        mb: 0,
      }}
    >
      {label}
      {helperText && (
        <Box
          sx={{
            fontSize: theme.typography.tiny.fontSize,
            color: `${theme.palette.purple[900]}AA`,
            mt: 0.25,
            textTransform: 'none',
          }}
        >
          {helperText}
        </Box>
      )}
    </Box>
  );
};

interface StyledTextFieldProps {
  label: string;
  value: string;
  onChange: (_value: string) => void;
  type?: string;
  inputProps?: any;
  fullWidth?: boolean;
  helperText?: string;
  boxSx?: SxProps;
  stepper?: number;
  prefix?: string;
  suffix?: string;
}

export const StyledTextField = ({
  label,
  value,
  onChange,
  type = 'text',
  inputProps = {},
  boxSx = {},
  fullWidth = true,
  helperText,
  stepper,
  prefix,
  suffix,
}: StyledTextFieldProps) => {
  const theme = useTheme();
  const fieldId = useId();

  const step = (direction: 1 | -1) => {
    const current = parseFloat(value) || 0;
    const next = parseFloat((current + direction * stepper!).toFixed(10));
    const min = inputProps?.min;
    const max = inputProps?.max;
    if (min !== undefined && next < min) return;
    if (max !== undefined && next > max) return;
    onChange(String(next));
  };

  const stepperButtonSx = {
    width: '1.5rem',
    borderRadius: 1,
    background: theme.palette.brand.primary,
    color: theme.palette.common.white,
    transition: 'background 0.2s ease, color 0.2s ease',
    fontSize: '1.3rem',
    fontWeight: 700,
    flexShrink: 0,
    '&:first-of-type': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    '&:last-of-type': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    '&:hover': {
      background: theme.palette.purple[700],
      color: theme.palette.common.white,
    },
    '&~.MuiTextField-root .MuiInputBase-root': {
      borderLeft: `none !important`,
      borderRight: `none !important`,
      borderRadius: `0 !important`,
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        ...boxSx,
      }}
    >
      <StyledLabel label={label} fieldId={fieldId} />
      {stepper !== undefined ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            <IconButton
              onClick={() => step(-1)}
              aria-label={`Decrease ${label}`}
              sx={stepperButtonSx}
              aria-controls={fieldId}
            >
              −
            </IconButton>
            <TextField
              id={fieldId}
              variant="outlined"
              fullWidth={fullWidth}
              type={type}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              sx={{
                flexGrow: 1,
                ...adornmentStyles(theme),
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              }}
              slotProps={{
                htmlInput: { ...inputProps, stepper: false },
                input: {
                  startAdornment: prefix ? (
                    <InputAdornment position="start">{prefix}</InputAdornment>
                  ) : undefined,
                  endAdornment: suffix ? (
                    <InputAdornment position="end">{suffix}</InputAdornment>
                  ) : undefined,
                },
              }}
            />
            <IconButton
              onClick={() => step(1)}
              aria-label={`Increase ${label}`}
              sx={stepperButtonSx}
              aria-controls={fieldId}
            >
              +
            </IconButton>
          </Box>
          {helperText && (
            <FormHelperText
              sx={{ color: theme.palette.common.black, fontSize: 14, mt: 0.5, mx: '14px' }}
            >
              {helperText}
            </FormHelperText>
          )}
        </>
      ) : (
        <TextField
          id={fieldId}
          variant="outlined"
          fullWidth={fullWidth}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={inputProps}
          sx={{ ...adornmentStyles(theme) }}
          helperText={helperText}
          slotProps={{
            input: {
              startAdornment: prefix ? (
                <InputAdornment position="start">{prefix}</InputAdornment>
              ) : undefined,
              endAdornment: suffix ? (
                <InputAdornment position="end">{suffix}</InputAdornment>
              ) : undefined,
            },
            ...(helperText
              ? {
                  formHelperText: {
                    sx: { color: theme.palette.common.black, fontSize: 14, mt: 0.5 },
                  },
                }
              : {}),
          }}
        />
      )}
    </Box>
  );
};

interface StyledSelectProps {
  label: string;
  value: string;
  onChange: (_value: string) => void;
  options: { value: string; label: string }[];
  fullWidth?: boolean;
}

export const StyledSelect = ({
  label,
  value,
  onChange,
  options,
  fullWidth = true,
}: StyledSelectProps) => {
  const fieldId = useId();

  return (
    <FormControl fullWidth={fullWidth} sx={{}}>
      <StyledLabel label={label} fieldId={fieldId} />
      <Select
        labelId={`${label.toLowerCase().replace(/\s+/g, '-')}-label`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          mt: 0.5,
          ...getCalculatorSelectStyles(),
        }}
        MenuProps={{
          PaperProps: {
            sx: (theme) => ({
              backgroundColor: theme.palette.common.white,
              color: theme.palette.text.primary,
              borderRadius: 5,
              boxShadow: theme.shadows[4],
              border: `1px solid ${theme.palette.purple[500]}`,
            }),
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface StyledToggleButtonFieldProps {
  label: string;
  value: string;
  onChange: (_value: string) => void;
  options: { value: string; label: string }[];
  fullWidth?: boolean;
  sx?: SxProps;
}

export const StyledToggleButtonField = ({
  label,
  value,
  onChange,
  options,
  fullWidth = true,
  sx = {},
}: StyledToggleButtonFieldProps) => {
  const theme = useTheme();
  const fieldId = useId();

  return (
    <FormControl
      fullWidth={fullWidth}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        ...sx,
      }}
    >
      <StyledLabel label={label} fieldId={fieldId} />
      <ToggleButtonGroup
        id={fieldId}
        value={value}
        onChange={(event: React.MouseEvent<HTMLElement>, _value: any) =>
          onChange((event?.target as HTMLInputElement).value)
        }
        sx={{
          '&.MuiToggleButtonGroup-root': {
            background: theme.palette.common.white,
            display: {
              xs: 'grid',
              lg: 'flex',
            },
            gridTemplateColumns: {
              xs: '50% 50%',
              lg: 'unset',
            },
            border: `1px solid ${theme.palette.purple[500]}`,
            overflow: 'hidden',
            borderRadius: '0.25rem',
          },
          '.MuiToggleButton-root': {
            background: theme.palette.common.white,
            borderColor: 'transparent',
            borderRadius: '0.2rem',
            color: theme.palette.brand.primary,
            px: 3,
            flexGrow: 1,
            fontSize: theme.typography.medium.fontSize,
            fontWeight: 600,
            transition: 'background 0.2s ease, color 0.2s ease',
            '&:hover': {
              background: theme.palette.purple[700],
              color: theme.palette.common.white,
            },
            '&.Mui-selected': {
              background: theme.palette.brand.primary,
              color: theme.palette.common.white,
              '&:hover': {
                background: theme.palette.brand.primary,
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            sx={{
              fontSize: theme.typography.large.fontSize,
              fontWeight: 600,
            }}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormControl>
  );
};

type StyledDatePickerProps = {
  label: string;
  /** Accepts YYYY-MM-DD string from state, emits YYYY-MM-DD string back */
  value: string;
  onChange: (_value: string) => void;
  fullWidth?: boolean;
  helperText?: string;
};

export const StyledDatePicker = ({
  label,
  value,
  onChange,
  fullWidth = true,
  helperText,
}: StyledDatePickerProps) => {
  const theme = useTheme();
  const fieldId = useId();

  // keep internal Dayjs state for proper typing behavior
  const [internalValue, setInternalValue] = React.useState<Dayjs | null>(
    value && dayjs(value, 'YYYY-MM-DD').isValid() ? dayjs(value, 'YYYY-MM-DD') : null
  );

  // when external value changes (e.g. reset form), sync it back in
  React.useEffect(() => {
    if (!value) return setInternalValue(null);
    const parsed = dayjs(value, 'YYYY-MM-DD');
    if (parsed.isValid()) setInternalValue(parsed);
  }, [value]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <StyledLabel label={label} fieldId={fieldId} />
        <DatePicker
          value={internalValue}
          onChange={(newVal) => {
            setInternalValue(newVal);
            onChange(newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '');
          }}
          slots={{ textField: TextField }}
          enableAccessibleFieldDOMStructure={false}
          slotProps={{
            textField: {
              fullWidth,
              helperText,
              InputLabelProps: {
                shrink: true,
                sx: getCalculatorDatePickerLabelStyles(theme),
              },
            },
            desktopPaper: {
              sx: {
                backgroundColor: theme.palette.common.white,
                borderRadius: 2,
                border: `1px solid ${theme.palette.purple[500]}`,
                boxShadow: theme.shadows[4],
              },
            },
            mobilePaper: {
              sx: {
                backgroundColor: theme.palette.common.white,
                borderRadius: 2,
                border: `1px solid ${theme.palette.purple[500]}`,
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

const formatSliderValue = (value: number) =>
  new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 }).format(value);

interface StyledTextSliderProps {
  label: string;
  helperText?: string;
  value: number;
  onChange: (_value: number) => void;
  minValue?: number;
  maxValue?: number;
  defaultMax?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  boxSx?: SxProps;
  textFieldSx?: SxProps;
  inputProps?: Partial<InputBaseProps['inputProps']>;
}

const sliderStyles = (theme: Theme) => ({
  color: theme.palette.brand.primary,
  mb: -0.5,
  '& .MuiSlider-thumb': {
    backgroundColor: theme.palette.common.white,
    border: `${theme.spacing(0.75)} solid ${theme.palette.brand.primary}`,
    boxShadow: theme.shadows[2],
  },
  '& .MuiSlider-track': {
    backgroundColor: theme.palette.brand.primary,
  },
  '& .MuiSlider-rail': {
    backgroundColor: theme.palette.purple[500],
    '&:hover': {
      backgroundColor: theme.palette.purple[600],
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.brand.primary,
    },
  },
});

const adornmentStyles = (_theme: Theme) => ({
  '& .MuiInputBase-inputAdornedStart': {
    pl: 0,
  },
  '& .MuiInputBase-inputAdornedEnd': {
    pr: 0,
  },
  '& .MuiInputAdornment-positionStart': {
    marginRight: 0.5,
  },
  '& .MuiInputAdornment-positionEnd': {
    marginLeft: 0.5,
  },
});

export type StyledSliderProps = {
  label: string;
  value: number;
  onChange: (_value: number) => void;
  step?: number;
  minValue?: number;
  maxValue?: number;
  prefix?: string;
  suffix?: string;
  boxSx?: SxProps;
  helperText?: string | undefined;
};

export const StyledTextSlider = ({
  label,
  helperText,
  value,
  onChange,
  defaultMax = 500000, // NOTE: if the default value in the useForm default values is greater than 500000 make sure to pass it as defaultMax
  minValue = 0,
  maxValue = undefined,
  step = 1,
  prefix,
  suffix,
  boxSx = {},
  textFieldSx = {},
  inputProps,
}: StyledTextSliderProps) => {
  const theme = useTheme();
  const fieldId = useId();
  const [sessionMax, setSessionMax] = React.useState(0);
  const [inputValue, setInputValue] = React.useState(value.toString());
  const [focused, setFocused] = React.useState(false);
  // Mirrors `value` but updates instantly while dragging, so the slider and its
  // linked text field stay responsive while the (debounced) onChange to the
  // form is still in flight.
  const [sliderValue, setSliderValue] = React.useState(value);
  const onChangeDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const sliderMax =
    maxValue !== undefined
      ? Math.min(maxValue, Math.max(defaultMax, sessionMax))
      : Math.max(defaultMax, sessionMax);

  React.useEffect(() => {
    if (!focused) {
      setInputValue(value.toString());
    }
  }, [value, focused]);

  React.useEffect(() => {
    setSliderValue(value);
  }, [value]);

  React.useEffect(() => {
    return () => {
      if (onChangeDebounceRef.current) clearTimeout(onChangeDebounceRef.current);
    };
  }, []);

  // Rapidly firing onChange on every drag step forces the whole form (and every
  // sibling field watching it) to re-render on every pixel of movement, which can
  // exceed React's update-depth limit when dragging quickly. Debounce the commit
  // to the form while keeping the visible thumb/value snappy via `sliderValue`.
  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const next = newValue as number;
    setSliderValue(next);
    if (onChangeDebounceRef.current) clearTimeout(onChangeDebounceRef.current);
    onChangeDebounceRef.current = setTimeout(() => {
      onChange(next);
    }, 250);
  };

  const handleSliderChangeCommitted = (
    _: Event | React.SyntheticEvent,
    newValue: number | number[]
  ) => {
    if (onChangeDebounceRef.current) clearTimeout(onChangeDebounceRef.current);
    onChange(newValue as number);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    setInputValue(raw);
  };

  const handleInputBlur = () => {
    setFocused(false);
    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      const clamped = maxValue !== undefined ? Math.min(parsed, maxValue) : parsed;
      if (clamped > sessionMax) setSessionMax(clamped);
      onChange(clamped);
    } else {
      setInputValue(value.toString());
    }
  };

  const displayInput = focused ? inputValue : formatSliderValue(sliderValue);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...boxSx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <StyledLabel label={label} fieldId={fieldId} helperText={helperText} />
        <TextField
          id={fieldId}
          variant="outlined"
          value={displayInput}
          onChange={handleInputChange}
          onFocus={() => setFocused(true)}
          onBlur={handleInputBlur}
          sx={{
            flexShrink: 1,
            width: '8rem',
            '& .MuiInputBase-root': {
              py: 1,
              px: 1.25,
            },
            '.MuiInputBase-input': {
              fontSize: theme.typography.h5.fontSize,
              fontWeight: 700,
              color: theme.palette.brand.primary,
              p: 0,
              flexShrink: 1,
              width: 'fit-content',
              textAlign: 'right',
            },
            '& .MuiInputAdornment-root *': {
              fontWeight: 700,
              color: theme.palette.brand.primary,
              fontSize: theme.typography.h5.fontSize,
            },
            ...adornmentStyles(theme),
            ...textFieldSx,
          }}
          slotProps={{
            htmlInput: {
              ...inputProps,
              inputMode: 'numeric',
              min: minValue,
              step: step,
            },
            input: {
              startAdornment: prefix ? (
                <InputAdornment position="start">{prefix}</InputAdornment>
              ) : undefined,
              endAdornment: suffix ? (
                <InputAdornment position="end">{suffix}</InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>
      <Slider
        min={minValue}
        max={sliderMax}
        step={step}
        value={Math.min(sliderValue, sliderMax)}
        onChange={handleSliderChange}
        onChangeCommitted={handleSliderChangeCommitted}
        sx={sliderStyles(theme)}
      />
    </Box>
  );
};

export const StyledSlider = ({
  label,
  value,
  onChange,
  step = 1,
  minValue = 0,
  maxValue = 500000,
  prefix,
  suffix,
  boxSx = {},
  helperText = undefined,
}: StyledSliderProps) => {
  const theme = useTheme();
  const fieldId = useId();

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    onChange(newValue as number);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...boxSx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <StyledLabel label={label} fieldId={fieldId} helperText={helperText} />
        <Typography
          sx={{
            fontSize: theme.typography.h5.fontSize,
            fontWeight: 700,
            color: theme.palette.brand.primary,
            lineHeight: 1.2,
            mt: 0.5,
          }}
        >
          {prefix}
          {formatSliderValue(value)}
          {suffix}
        </Typography>
      </Box>
      <Slider
        id={fieldId}
        min={minValue}
        max={maxValue}
        step={step}
        value={value}
        onChange={handleSliderChange}
        sx={sliderStyles(theme)}
      />
    </Box>
  );
};
